import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import SupportDialog from '../components/SupportDialog';
import LegalLinks from '../components/legal/LegalLinks';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BIREY_ISSUER = process.env.REACT_APP_BIREY_ISSUER || 'http://localhost:8790';
const BIREY_LOGOUT = `${BIREY_ISSUER}/oauth/logout`;
const ADMIN_PANEL_URL = process.env.REACT_APP_ADMIN_PANEL_URL || '';

// Human-readable message for a ?birey_error=… returned by the Birey OAuth flow.
function bireyErrorMessage(code: string): string {
  switch (code) {
    case 'no_entitlement':
      return 'Kurumunuzun eis ekosis LMS erişimi bulunmuyor.';
    // Refused by Birey Kimlik itself, BEFORE any code was issued — this account is
    // estate staff (süper admin / admin) and the student app is closed to it.
    //
    // ⚠️ Deliberately NOT "çıkış yapıp tekrar deneyin". Signing out and back in as
    // the SAME person is refused identically, so that wording sends someone round a
    // loop and blames them for it. Logging out only helps if the next sign-in is a
    // DIFFERENT, non-staff person — which is why it is offered as one of two
    // choices below the message rather than as the instruction.
    case 'staff_account':
      return 'Bu bir yönetici hesabıdır. eis ekosis LMS öğrenci uygulamasına yönetici hesabıyla giriş yapılamaz.';
    case 'auth_failed':
    case 'token_exchange_failed':
    // Token endpoint, RFC 6749 §5.2 — a consumed or expired authorization code,
    // or the post-grant re-read of person status / membership. Surfaced only
    // since birey.routes.ts started passing the IdP's own code through instead
    // of flattening everything to token_exchange_failed. "Try again" is the safe
    // superset: right for the expiry case, harmless for the others.
    case 'invalid_grant':
      return 'Birey ile giriş başarısız oldu, lütfen tekrar deneyin.';
    // Misconfiguration on our side (client or redirect_uri not registered as
    // expected). Retrying can never help, so do not invite it.
    case 'invalid_client':
    case 'unauthorized_client':
    case 'invalid_request':
      return 'Giriş yapılandırması hatalı, lütfen kurum yöneticinizle görüşün.';
    case 'invalid_state':
      return 'Giriş doğrulanamadı, lütfen tekrar deneyin.';
    case 'account_suspended':
      return 'Hesabınız pasif durumda. Lütfen kurum yöneticinizle görüşün.';
    case 'use_admin_login':
      return 'Yönetici hesapları buradan giriş yapamaz. Lütfen yönetim panelini kullanın.';
    case 'identity_conflict':
      return 'Hesap eşleştirmesinde bir tutarsızlık tespit edildi. Lütfen kurum yöneticinizle görüşün.';
    // Verified at Birey, but the role has no place in this app (see
    // utils/appRoles.ts). Previously this landed on a blank screen.
    case 'unsupported_role':
      return 'Hesabınızın rolü bu uygulamada desteklenmiyor. Lütfen kurum yöneticinizle görüşün.';
    default:
      return 'Birey ile giriş yapılamadı.';
  }
}

const mascotSlides = [
  { image: '/a-mascot1.png', text: 'Dijital eğitim platformu ile öğrenmenin keyfini çıkarın.', offsetX: 0, offsetY: 0 },
  { image: '/a-mascot2.png', text: 'Derslerinizi takip edin, ödevlerinizi tamamlayın.', offsetX: 30, offsetY: -10 },
  { image: '/a-mascot3.png', text: 'Başarıya giden yolda size eşlik ediyoruz.', offsetX: 0, offsetY: 0 },
  { image: '/a-mascot4.png', text: 'Hedeflerinize ulaşmak için birlikte çalışalım.', offsetX: 0, offsetY: 0 },
  { image: '/a-mascot5.png', text: 'Digikoç ile akıllı öğrenme deneyimi yaşayın.', offsetX: 0, offsetY: 0 },
];

const Login: React.FC = () => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bireyErrorCode = searchParams.get('birey_error');

  // Auto-advance carousel every 4.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mascotSlides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left - White side. Form genişliği ve dikey boşluklar küçültüldü (#1):
          %100 zoom altında bile dengeli görünmesi için tek bir merkezi
          max-width veriyoruz, input yükseklikleri ~10px azaltıldı, font
          adımları ~2-4px küçüldü. */}
      <div className="w-full lg:w-[45.5%] min-h-screen bg-[#fafafa] flex flex-col relative">
        {/* Logo - centered */}
        <div className="flex justify-center pt-[40px] pb-[24px] lg:pt-[100px] lg:pb-[100px]">
          {/* Login has room, so this is the one place the full lockup earns its keep;
              raised from 44/52 to 64/76 so the "eis ekosis" wordmark is actually readable. */}
          <BrandLogo variant="full" className="h-[64px] lg:h-[90px] xl:h-[100px] object-contain" />
        </div>

        {/* Form area - centered with proper padding */}
        <div className="flex-1 flex flex-col items-center px-6 sm:px-12 lg:px-[60px] pb-6">
          <div className="w-full max-w-[460px]">
          {/* Title */}
          <h1 className="text-[20px] lg:text-[22px] font-semibold text-[#2b2b2b] mb-[6px]">Giriş Yap</h1>

          <div className="flex flex-col">
            <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, lineHeight: 1.5, color: '#5b6472', marginBottom: 20 }}>
              Uygulamaya eis ekosis hesabınızla giriş yapın.
            </p>

            {/* eis ekosis ile Giriş — central-IdP single sign-on across the ecosystem.
                Only the brand on the button changed: the URL, the OAuth client and the
                IdP behind them are untouched, so this is a label swap, not a new door. */}
            <div className="h-[8px] lg:h-[10px]" />
            <button
              type="button"
              onClick={() => { window.location.href = `${API_URL}/auth/birey/login`; }}
              className="w-full h-[46px] lg:h-[52px] bg-white text-[#1d1d1b] text-[15px] lg:text-[17px] font-semibold rounded-[16px] border-2 border-[#0b77ff] hover:bg-[#eff6ff] transition-colors flex items-center justify-center gap-[10px]"
            >
              {/* The wordmark IS the subject of "… ile Giriş Yap", so it carries alt text
                  instead of aria-hidden — the old birey mark was decorative next to a
                  full sentence, this one is the sentence's first word. Without it a
                  screen reader announces a bare "ile Giriş Yap".

                  -translate-y is optical alignment, not a nudge. The PNG is cropped
                  tight to its ink, and in "eisekosis" the top third is nothing but the
                  pink dot and the k's ascender — no descenders below. So the letter
                  bodies (rows 102-345 of 358) sit 12.4% of the image height BELOW the
                  box centre, and centring the BOX would hang the word visibly low.

                  The shipped 9% is NOT that raw 12.4%: it was measured in the browser
                  against the neighbouring text's own x-height centre. 12.4% overshoots
                  and leaves the wordmark 0.6px high; 9% lands it at 0.01px. Re-measure
                  the same way if the asset is recropped or the type size changes. */}
              <img
                src="/eisekosis-logo.png"
                alt="eis ekosis"
                className="h-[16px] lg:h-[18px] w-auto object-contain -translate-y-[9%]"
              />
              ile Giriş Yap
            </button>

            {/* Videos Button */}
            <div className="h-[8px] lg:h-[10px]" />
            <button
              type="button"
              onClick={() => navigate('/deneme-sinavi-videolari')}
              className="w-full h-[46px] lg:h-[52px] bg-[#fafafa] text-[#e5599b] text-[15px] lg:text-[17px] font-semibold rounded-[16px] border border-[#e5599b] hover:bg-pink-50 transition-colors"
            >
              Deneme Sınavı Videoları
            </button>
          </div>

          {/* Help — <p> değil <button>: satır zaten tıklanabilir GÖRÜNÜYORDU
              (cursor-pointer + hover:underline) ama hiçbir şey yapmıyordu. Artık
              destek penceresini açtığına göre gerçek bir düğme olmalı; <p> klavyeyle
              odaklanamaz, Enter/Space almaz ve ekran okuyucuya düğme olduğunu
              söylemez. `w-full` başlığın ortalanmasını koruyor. */}
          <div className="h-[14px] lg:h-[18px]" />
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={helpOpen}
            className="w-full text-center text-[#2b2b2b] text-[13px] lg:text-[15px] font-semibold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3973bf] rounded-[8px]"
          >
            Yardıma ihtiyacın var mı?
          </button>

          {/* Reddedilme mesajı butonların ALTINDA, o zaten boş duran alanda.
              Form akışının içindeyken mesaj belirdiği anda altındaki her şeyi
              aşağı itiyordu: reddedilen kişi, yeniden denemek için uzandığı
              düğmenin ayağının altından kaydığını görüyordu. Burada sayfa hiç
              oynamıyor, mesaj da kaybolmuyor — sütunun boşluğu onu taşıyor.
              Kutunun genişliği butonlarla aynı (max-w-[460px] içinde). */}
          {bireyErrorCode && (
            <div
              role="alert"
              className="mt-[22px] lg:mt-[28px] rounded-[16px] border border-[#f2c7c2] bg-[#fdeceb] px-[20px] py-[14px]"
            >
              <p className="text-[14px] lg:text-[15px] font-semibold leading-relaxed text-[#b3261e]">
                {bireyErrorMessage(bireyErrorCode)}
              </p>
            </div>
          )}

          {/* A refusal the person can act on — the message alone leaves them stuck
              on the page that just rejected them, with the Birey session that
              caused it still live and invisible.

              Kasıtlı olarak AYRI bir kutu ve ayrı renk: üstteki kırmızı olan ret,
              bu ise ne yapılacağı. İkisini tek kutuda birleştirmek yönlendirmeyi
              hatanın devamı gibi okutuyordu.

              Both links open in a NEW TAB on purpose: signing out of Birey Kimlik
              is a detour, not a destination, and this tab is where they come back
              to. `rel="noopener noreferrer"` because target="_blank" otherwise
              hands the opened page a reference to this one. */}
          {bireyErrorCode === 'staff_account' && (
            <div className="mt-[10px] rounded-[16px] border border-amber-200 bg-amber-50 px-[20px] py-[14px] text-[13px] leading-relaxed text-[#5b6472]">
              {ADMIN_PANEL_URL && (
                <p>
                  Yönetici işlemleri için{' '}
                  <a
                    href={ADMIN_PANEL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#3973bf] hover:underline"
                  >
                    LMS Yönetim Paneli
                  </a>
                  'ni kullanın.
                </p>
              )}
              <p className={ADMIN_PANEL_URL ? 'mt-1' : ''}>
                Öğrenci hesabıyla giriş yapmak istiyorsanız önce{' '}
                <a
                  href={BIREY_LOGOUT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#3973bf] hover:underline"
                >
                  Birey Kimlik oturumunuzu kapatın
                </a>
                , sonra bu sekmede tekrar deneyin.
              </p>
            </div>
          )}

          </div>

        </div>

        {/* Aydınlatma metni giriş ekranından erişilebilir olmak zorunda:
            KVKK m.10 aydınlatmayı veri işlemenin BAŞLAMASINDAN önceye
            bağlıyor, giriş yapmış kullanıcıya göstermek geç kalıyor.

            Form alanının İÇİNDE değil, sütunun dibinde: form kutusunun altında
            dururken ekranın ortasında boşta asılı kalıyordu. Üstündeki alan
            `flex-1` olduğu için bu satır beyaz sütunun tabanına oturuyor. */}
        <div className="px-6 sm:px-12 lg:px-[60px] pb-6">
          <LegalLinks />
        </div>
      </div>

      {/* Right - Blue side */}
      <div className="hidden lg:flex flex-1 bg-[#3973bf] relative overflow-hidden flex-col items-center">
        {/* Tiling pattern background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('/a-tilingback.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'color-burn',
            opacity: 0.7,
          }}
        />

        {/* Stage-light bloom keyframes (a UA-hidden <style>, so no layout impact).
            The glow re-blooms once per slide; disabled under reduced-motion. */}
        <style>{`
          @keyframes stageBloom {
            0%   { opacity: 0.5; transform: scale(0.95); }
            45%  { opacity: 1;   transform: scale(1.03); }
            100% { opacity: 1;   transform: scale(1); }
          }
          .stage-bloom { animation: stageBloom 700ms ease-out; }
          @media (prefers-reduced-motion: reduce) {
            .stage-bloom { animation: none; }
          }
        `}</style>

        {/* Mascot carousel — "spotlight stage". Now the TOP element, so its masked
            bottom dissolves down INTO the tagline + dots below it rather than into
            empty blue. The a-mascot*.png renders are bust crops whose content runs to
            the image's bottom edge, so a raw <img> on the flat blue would show that
            crop as a hard horizontal cut. Fix: (1) mask the bottom so the crop
            dissolves, (2) sit the mascot in a dim radial stage-glow that re-blooms on
            each slide, (3) size by viewport height (clamp) so placement stays stable
            across laptop vs monitor. flex-1 + items-end rests the mascot just above the
            text, with the leftover space as top breathing room. */}
        <div className="relative z-10 flex-1 flex items-end justify-center w-full min-h-0 pb-[32px]">
          <div className="relative w-full max-w-[820px] h-[clamp(440px,72vh,780px)]">
            {/* Stage-light bloom behind the mascot; re-blooms via key remount */}
            <div
              key={`glow-${currentSlide}`}
              aria-hidden
              className="stage-bloom pointer-events-none absolute inset-0"
              style={{
                transformOrigin: '50% 70%',
                // Dim + fully faded to 0 well inside the box, so the glow div's bottom
                // edge has nothing left to show as a hard line. Keep the transparent
                // stop (70%) < box-bottom-in-radius.
                background:
                  'radial-gradient(56% 36% at 50% 66%, rgba(255,255,255,0.16) 0%, rgba(205,222,255,0.05) 42%, rgba(57,115,191,0) 70%)',
              }}
            />
            {mascotSlides.map((slide, i) => (
              <img
                key={i}
                src={slide.image}
                alt={`Mascot ${i + 1}`}
                className="absolute inset-0 w-full h-full object-contain object-bottom transition-opacity duration-700"
                style={{
                  opacity: i === currentSlide ? 1 : 0,
                  transform: `translate(${slide.offsetX}px, ${slide.offsetY}px)`,
                  WebkitMaskImage: 'linear-gradient(to bottom, #000 70%, transparent 90%)',
                  maskImage: 'linear-gradient(to bottom, #000 70%, transparent 90%)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Info text - changes per slide; sits just under the mascot's fade */}
        <div className="relative z-10 text-center px-[60px] xl:px-[80px] mb-[16px]">
          <p className="text-[#fafafa] text-[20px] xl:text-[24px] font-bold leading-relaxed transition-opacity duration-500">
            {mascotSlides[currentSlide].text}
          </p>
        </div>

        {/* Slider dots — now the bottom element */}
        <div className="relative z-10 mb-[48px] flex justify-center gap-[20px]">
          {mascotSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className="w-[15px] h-[15px] rounded-full transition-colors duration-300"
              style={{ backgroundColor: i === currentSlide ? '#fafafa' : 'rgba(255,255,255,0.5)' }}
              aria-label={`Görsel ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <SupportDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};

export default Login;
